import { buildComponent, isComponentPropsValid } from '#components'
import { correctUrlProtocol } from '#libs'
import { get, isEmpty } from 'lodash'
import { z } from 'zod'
import * as store from './product-customization-board.store'
import {
  createVirtualLayersEl,
  getLayerColorsFromQueryParams,
  processLayersData,
  processLayersVisualization,
  renderLivePreview,
  renderPreviewFile,
  updateLayersQueryParams
} from './product-customization-board.utils'
import ProductCustomizationBoardVue from './ProductCustomizationBoard.vue'

const { reset: resetCustomizationStore, ...atoms } = store
const {
  $inputLayersUrl,
  $inputVariantLayers,
  $initialLayerColors,
  $outputLayers,
  $previewResolutionWidth,
  $previewWatermarkHelpText,
  $previewWatermarkImageUrl,
  $printSimulationDataUri,
  $printSimulationErrorText,
  $printSimulationHelpText,
  $printSimulationWarnText,
  $reviewConfirmationAgreed,
  $reviewConfirmationMessage,
  $selectedVariantId
} = atoms

let removeVariantChangeSubscriber: (() => void) | null = null
let removeInputLayersUrlSubscriber: (() => void) | null = null
let removeOutputLayerSubscriber: (() => void) | null = null

const ProductCustomizationModalPropsSchema = z.object({
  selectedVariantId: z.string(),
  inputVariantLayers: z.record(z.string()).transform((val) => {
    Object.entries(val).forEach(([key, value]) => {
      val[key] = correctUrlProtocol(value)
    })

    return val
  }),
  initialLayerColors: z.record(z.string()),
  previewResolutionWidth: z.number().int(),
  previewWatermarkImageUrl: z.string().transform((val) => correctUrlProtocol(val)),
  previewWatermarkHelpText: z.string(),
  printSimulationHelpText: z.string(),
  printSimulationWarnText: z.string(),
  printSimulationErrorText: z.string(),
  reviewConfirmationMessage: z.string()
})

const ProductCustomizationBoard = buildComponent({
  name: 'product-customization-board',
  propsSchema: ProductCustomizationModalPropsSchema,
  vueComponent: ProductCustomizationBoardVue,
  debugStores: atoms,
  utilities: {
    getOutputFormData
  },
  async onConnected(component) {
    if (!isComponentPropsValid(component)) return

    saveCustomizationSettings(component.props)
    attachStoreEvents()
  },
  onDisconnected() {
    removeVariantChangeSubscriber?.()
    removeInputLayersUrlSubscriber?.()
    removeOutputLayerSubscriber?.()
    resetCustomizationStore()
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'product-customization-board': typeof ProductCustomizationBoard
  }
}

function attachStoreEvents() {
  removeVariantChangeSubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
    const variantId = get(data, 'data.variant.id', '')

    $selectedVariantId.set(variantId)
    $printSimulationDataUri.set('')
    $reviewConfirmationAgreed.set(false)
  })

  removeInputLayersUrlSubscriber = $inputLayersUrl.subscribe(async () => {
    await createVirtualLayersEl()
    processLayersData()
  })

  removeOutputLayerSubscriber = $outputLayers.subscribe(async () => {
    updateLayersQueryParams()
    processLayersVisualization()
    await renderLivePreview()

    $printSimulationDataUri.set('')
  })
}

function saveCustomizationSettings(props: z.infer<typeof ProductCustomizationModalPropsSchema>) {
  $selectedVariantId.set(props.selectedVariantId)
  $inputVariantLayers.set(props.inputVariantLayers)
  $initialLayerColors.set(
    isEmpty(props.initialLayerColors) ? getLayerColorsFromQueryParams() : props.initialLayerColors
  )
  $previewResolutionWidth.set(props.previewResolutionWidth)
  $previewWatermarkImageUrl.set(props.previewWatermarkImageUrl)
  $previewWatermarkHelpText.set(props.previewWatermarkHelpText)
  $printSimulationHelpText.set(props.printSimulationHelpText)
  $printSimulationWarnText.set(props.printSimulationWarnText)
  $printSimulationErrorText.set(props.printSimulationErrorText)
  $reviewConfirmationMessage.set(props.reviewConfirmationMessage)
}

async function getOutputFormData() {
  const previewFile = await renderPreviewFile()

  if (!previewFile) return

  const productInfoEl = document.querySelector<HTMLElement>('product-info')

  if (!productInfoEl) return

  const formData = new FormData()

  formData.append('properties[_customization_active]', 'true')
  formData.append('properties[_customization_preview_url]', previewFile)

  const productUrl = productInfoEl.dataset.url
  const currentQueryStr = window.location.search

  // We know that query string is always added to URL within updateLayersQueryParams function
  formData.append('properties[_customization_product_url]', `${window.location.origin}${productUrl}${currentQueryStr}`)

  const layers = $outputLayers.get()

  layers.forEach((layer) => {
    const { fieldName, color } = layer

    formData.append(`properties[${fieldName}]`, color)
  })

  return formData
}

export * from './product-customization-board.store'
export * from './product-customization-board.utils'
